/**
 * Identity rules for matching persisted shortlist / itinerary items by their stored `metadata`.
 *
 * Shared (not re-derived) so web and native decide "is this the same item?" the same way — the
 * shortlist toggle, rate picker, and itinerary membership checks all hinge on it. No React /
 * Firebase imports (React-Native / Metro-bundlable per the `@elara/domain` portability guard).
 */

import type { Occupancy } from './models/schemas/room-schemas.js';

import { occupancyKey } from './models/schemas/room-schemas.js';

/**
 * Reduces a metadata blob to the keys that define an item's persisted identity.
 *
 * Room items come in two kinds:
 * - A room at a RATE (itinerary items, rate cards, checkout, and shortlist items pinned to a rate) is
 *   identified by `(productCode, occupancy)`: Sabre rotates `rateKey` on every request so it can never
 *   be persisted identity, and switching occupancy in the rate picker changes which stored item a
 *   toggle targets.
 * - A room with no `productCode` (an unpinned shortlist item) is identified by its `referenceId`
 *   alone. Its `occupancy` is only the party to price it for, so it returns `undefined` ("any entry
 *   for this referenceId", pinned or not); `quantity` must not split identity either.
 *
 * Non-room items pass through unchanged.
 */
export function metadataForIdentity(
  itemType: string,
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (metadata == null) return undefined;
  if (itemType === 'room') {
    if (typeof metadata.productCode !== 'string') return undefined;
    const out: Record<string, unknown> = { productCode: metadata.productCode };
    if (metadata.occupancy && typeof metadata.occupancy === 'object') {
      out.occupancy = metadata.occupancy;
    }
    return out;
  }
  return metadata;
}

/**
 * Returns true when `itemMetadata` matches every key in `target`.
 *
 * `occupancy` is compared by its serialized {@link occupancyKey} (so order-equivalent objects —
 * e.g. reordered `childrenAges` — still match); all other keys use strict equality. A `target`
 * occupancy with no counterpart on the item never matches.
 */
export function metadataMatches(itemMetadata: unknown, target: Record<string, unknown>): boolean {
  const meta = (itemMetadata ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(target)) {
    if (key === 'occupancy') {
      const a = meta.occupancy as Occupancy | undefined;
      const b = target.occupancy as Occupancy | undefined;
      if (!a || !b) return false;
      if (occupancyKey(a) !== occupancyKey(b)) return false;
      continue;
    }
    if (meta[key] !== target[key]) return false;
  }
  return true;
}

/**
 * A room item's pinned rate as a comparable key — `productCode|occupancyKey` — or `''` when it isn't
 * pinned. Two room entries for the same `referenceId` in one shortlist group are the same entry only
 * when these match, so a group can hold a room unpinned and the same room pinned to a rate as two
 * scenarios, but never the same pin twice. Server dedupe and the board's drag check share this rule.
 */
export function roomPinKey(metadata: unknown): string {
  const meta = (metadata ?? {}) as { productCode?: unknown; occupancy?: unknown };
  if (typeof meta.productCode !== 'string' || meta.productCode === '') return '';
  const occupancy = meta.occupancy && typeof meta.occupancy === 'object' ? occupancyKey(meta.occupancy as Occupancy) : '';
  return `${meta.productCode}|${occupancy}`;
}

/**
 * Whether two shortlist entries are the same entry, which a group may hold only once: same type and
 * `referenceId` and, for a room, the same pin (see `roomPinKey`). An unpinned room and that room pinned
 * to a rate are different scenarios and may share a group. The rule the server dedupes adds by, and the
 * one Move/Copy and drag use to decide which groups already hold an entry.
 */
export function isSameShortlistEntry(
  a: { type: string; referenceId?: string | null; metadata?: unknown },
  b: { type: string; referenceId?: string | null; metadata?: unknown },
): boolean {
  if (a.type !== b.type || a.referenceId !== b.referenceId) return false;
  if (a.type !== 'room') return true;
  return roomPinKey(a.metadata) === roomPinKey(b.metadata);
}
